import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pwaApi } from '../../lib/api.js';
import { useSessionStore } from '../../store/sessionStore.js';

interface MachineRef {
  id: number;
  name: string;
  code: string;
}

type FatalError = 'IP_NOT_WHITELISTED' | 'AUTH_FAILED' | null;

export default function ScanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loadContext } = useSessionStore();
  const [machineCode, setMachineCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fatalError, setFatalError] = useState<FatalError>(null);
  const [reassign, setReassign] = useState<{ currentMachine: MachineRef; newMachine: MachineRef; payload: Record<string, unknown> } | null>(null);

  async function attemptProvision(payload: Record<string, unknown>) {
    setLoading(true);
    setError('');
    try {
      await pwaApi.post('/tablet/provision', payload);
      await loadContext();
      navigate('/workstation', { replace: true });
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { error?: { code?: string; details?: unknown } } } };
      const code = err.response?.data?.error?.code;
      const status = err.response?.status;

      if (status === 409 && code === 'REASSIGN_CONFIRMATION_REQUIRED') {
        const details = err.response?.data?.error?.details as { currentMachine: MachineRef; newMachine: MachineRef } | undefined;
        if (details) setReassign({ ...details, payload });
        else setError('Riabbinamento richiede conferma, ma i dettagli non sono disponibili.');
      } else if (status === 403 && code === 'IP_NOT_WHITELISTED') {
        setFatalError('IP_NOT_WHITELISTED');
      } else if (status === 401) {
        setFatalError('AUTH_FAILED');
      } else if (code === 'MACHINE_NOT_FOUND') {
        setError('Macchina non trovata. Verificare il codice o il QR.');
      } else if (code === 'PLANT_MISMATCH') {
        setError('Questa macchina appartiene a uno stabilimento diverso da quello del tablet. Impossibile abbinare.');
      } else if (code === 'TABLET_INACTIVE') {
        setError('Questo tablet è stato revocato. Contattare IT.');
      } else if (code === 'MACHINE_CODE_AMBIGUOUS') {
        setError('Più macchine condividono questo codice in questo stabilimento. Usare il QR invece del codice manuale.');
      } else if (code === 'VALIDATION_ERROR') {
        setError('Richiesta non valida.');
      } else {
        setError('Errore di connessione. Riprovare.');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const machineId = searchParams.get('machineId');
    if (machineId) {
      attemptProvision({ machineId: Number(machineId) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleManualProvision() {
    if (!machineCode.trim()) { setError('Inserire il codice macchina.'); return; }
    attemptProvision({ machineCode: machineCode.trim().toUpperCase() });
  }

  function handleConfirmReassign() {
    if (!reassign) return;
    const payload = { ...reassign.payload, confirm: true };
    setReassign(null);
    attemptProvision(payload);
  }

  if (fatalError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#1a1a2e' }}>
        <div className="w-full max-w-md bg-gray-900 border border-red-800 rounded-xl p-8 text-center space-y-3">
          <div className="text-red-400 font-bold text-xl">
            {fatalError === 'IP_NOT_WHITELISTED' ? 'Accesso non consentito da questa rete' : 'Certificato non valido'}
          </div>
          <p className="text-gray-400 text-sm">
            {fatalError === 'IP_NOT_WHITELISTED'
              ? 'Questo tablet non risulta connesso dalla rete autorizzata per il suo stabilimento. Contattare IT.'
              : 'Il certificato del tablet non è valido o è stato revocato. Contattare IT.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#1a1a2e' }}>
      <div className="w-full max-w-lg">
        <div className="text-center mb-10">
          <div className="text-white font-bold text-3xl mb-2">BIOFARMA MONITOR</div>
          <div className="text-gray-400 text-lg">Abbinamento tablet a macchina</div>
        </div>

        <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 space-y-4">
          <p className="text-gray-300 text-sm text-center">
            Inquadra il QR code affisso alla macchina, oppure inserisci manualmente il codice.
          </p>

          <div className="aspect-square bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center">
            <div className="text-center text-gray-500">
              <div className="w-12 h-12 border-2 border-gray-500 rounded mx-auto mb-2" />
              <p className="text-sm">Scanner QR — usa la fotocamera di sistema</p>
            </div>
          </div>

          <div>
            <label className="block text-gray-300 font-bold text-sm mb-2">Codice Macchina</label>
            <input
              className="w-full bg-gray-800 text-white text-lg px-4 py-3 rounded border border-gray-600 focus:border-blue-500 focus:outline-none uppercase"
              placeholder="es. LCC-01"
              value={machineCode}
              onChange={e => setMachineCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleManualProvision()}
            />
          </div>

          {error && (
            <div className="bg-red-900 border border-red-700 rounded-lg p-3">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          <button
            onClick={handleManualProvision}
            disabled={loading || !machineCode.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 text-white font-bold py-4 rounded-lg transition-colors text-lg"
          >
            {loading ? 'Connessione...' : 'ABBINA MACCHINA'}
          </button>
        </div>
      </div>

      {reassign && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50">
          <div className="w-full max-w-md bg-gray-900 border border-yellow-700 rounded-xl p-6 space-y-4">
            <div className="text-yellow-400 font-bold text-lg">Riabbinamento richiesto</div>
            <p className="text-gray-300 text-sm">
              Questo tablet è attualmente abbinato a <strong>{reassign.currentMachine.name}</strong> ({reassign.currentMachine.code}).
              Vuoi riabbinarlo a <strong>{reassign.newMachine.name}</strong> ({reassign.newMachine.code})?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setReassign(null)}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleConfirmReassign}
                disabled={loading}
                className="flex-1 bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-700 text-white font-bold py-3 rounded-lg"
              >
                {loading ? 'Attendere...' : 'Conferma riabbinamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
